import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAnimalType, ANIMAL_TYPES } from "../../contexts/AnimalTypeContext";
import { toast } from "sonner";
import api from "../../lib/api";
import { applyAsInfluencer } from "../../lib/whatsapp";
import Countdown from "./Countdown";
import LiveCounter from "./LiveCounter";
import {
  Bell, Search, Users, Camera, ClipboardCheck, Gavel, Calendar, Crown,
  ChevronLeft, Scale, Eye, Trophy, Stethoscope, LogIn, BadgeCheck, Award, Package,
  Sprout, Megaphone, FileText, BookOpen, Repeat, Settings as SettingsIcon,
  ShoppingBag, Plus, ArrowLeft,
} from "lucide-react";
import AnimalTypePicker from "./AnimalTypePicker";
import ThemeSwitcher from "./ThemeSwitcher";
import InfluencerStatusCard from "./InfluencerStatusCard";

/**
 * Home dashboard — animal-type aware 12-tile grid.
 *
 * Flow:
 *   1) First visit → AnimalTypePicker (5 cards). User picks a type.
 *   2) Type persisted in localStorage. Dashboard renders 12 service tiles for that type.
 *   3) Top pill shows current animal; tap → re-open picker.
 *   4) Mazayen / Races toggle on the dashboard (races hidden for sheep/goat).
 */
export default function MobileHomeDashboard() {
  const { t, lang, toggle } = useI18n();
  const { user } = useAuth();
  const { type, setType } = useAnimalType();
  const ar = lang === "ar";

  const [pickerOpen, setPickerOpen] = useState(false);
  const [stats, setStats] = useState({ users: 0, animals: 0, evaluations: 0, experts: 0 });
  const [statsBase, setStatsBase] = useState(null); // snapshot at last poll
  const [activities, setActivities] = useState([]);
  const [champStats, setChampStats] = useState({ total_interest: 0, earliest_opens_at: null });

  useEffect(() => {
    const loadStats = () => {
      api.get("/public/stats").then((r) => {
        setStats(r.data);
        setStatsBase({ data: r.data, at: Date.now() });
      }).catch(() => {});
    };
    loadStats();
    api.get("/public/recent-activity").then((r) => setActivities(r.data || [])).catch(() => {});
    api.get("/championships/stats").then((r) => setChampStats(r.data)).catch(() => {});
    // Refresh real counters every 2 minutes
    const poll = setInterval(loadStats, 120_000);
    return () => clearInterval(poll);
  }, []);

  // Live-increment: between polls, add small deltas so the counter breathes
  // visibly. Rates chosen so it feels like a busy live platform without
  // outpacing the backend social-proof baseline over ~2 minute polling.
  useEffect(() => {
    if (!statsBase) return;
    // Fractional deltas per second — cumulative, only rendered as integers.
    const rates = { users: 1 / 8, animals: 1 / 40, evaluations: 1 / 15, experts: 0 };
    const startAt = Date.now();
    const tick = setInterval(() => {
      const secs = (Date.now() - startAt) / 1000;
      setStats({
        users:       statsBase.data.users       + Math.floor(rates.users * secs),
        animals:     statsBase.data.animals     + Math.floor(rates.animals * secs),
        evaluations: statsBase.data.evaluations + Math.floor(rates.evaluations * secs),
        experts:     statsBase.data.experts,
      });
    }, 2_000); // repaint every 2s
    return () => clearInterval(tick);
  }, [statsBase]);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (isNaN(ms)) return "—";
    const m = Math.floor(ms / 60000);
    if (m < 1) return ar ? "الآن" : "now";
    if (m < 60) return ar ? `قبل ${m} دقيقة` : `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return ar ? `قبل ${h} ساعة` : `${h}h ago`;
    const d = Math.floor(h / 24);
    return ar ? `قبل ${d} يوم` : `${d}d ago`;
  };

  // -------- First-time picker (or user reopens it) ---------------------------
  if (!type || pickerOpen) {
    return <AnimalTypePicker onPick={() => setPickerOpen(false)} />;
  }

  const animalMeta = ANIMAL_TYPES.find((a) => a.key === type) || ANIMAL_TYPES[0];

  // -------- Tiles per type. Racing mode has been removed. -------------------
  const comingSoonBadge = ar ? "قريباً" : "Soon";
  const mazayenTiles = [
    { key: "subscribe",   icon: BadgeCheck,    to: "/subscribe",    label: t("svc_subscribe"), badge: t("svc_subscribe_badge") },
    { key: "grow",        icon: Sprout,        to: "/grow",         label: t("svc_grow"),      badge: t("svc_grow_badge") },
    { key: "evaluate",    icon: ClipboardCheck,to: "/official-eval", label: ar ? "تقييم ثقة الرسمي" : "Thqah Official Eval", badge: ar ? "رسمي" : "Official" },
    { key: "tamper",      icon: Eye,           to: "/tamper",       label: t("svc_tamper") },
    { key: "vet",         icon: Package,       to: "/thqah-products", label: ar ? "منتج ثقة" : "Thqah Products" },
    // Tile: Become an influencer — opens WhatsApp directly
    { key: "influencers", icon: Megaphone,     to: "#",             label: ar ? "كن مؤثراً" : "Become Influencer", external: "whatsapp_influencer" },
    // Tile: Document livestock
    { key: "document",    icon: FileText,      to: "/document",     label: t("svc_document") },
    // Tile: Beauty standards / pedigree
    { key: "specs",       icon: BookOpen,      to: "/specs",        label: t("svc_specs") },
  ];

  const baseTiles = mazayenTiles;

  return (
    <div className="min-h-screen mobile-dashboard pb-4" data-testid="mobile-home-dashboard">
      {/* Header */}
      <header className="px-5 pt-6 pb-4 flex items-start justify-between" dir={ar ? "rtl" : "ltr"}>
        {user ? (
          <Link
            to="/account"
            className="h-10 rounded-full bg-white/5 border border-[#D4AF37]/30 overflow-hidden flex items-center gap-2 ps-1 pe-3"
            data-testid="mhd-profile"
            aria-label={ar ? "حسابي" : "My account"}
          >
            <span className="h-8 w-8 rounded-full overflow-hidden bg-black flex items-center justify-center shrink-0">
              {user.avatar_url
                ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
                : <Crown className="h-4 w-4 text-[#D4AF37]" />}
            </span>
            <span className="text-[11px] font-bold text-[#D4AF37]">{ar ? "حسابي" : "Account"}</span>
          </Link>
        ) : (
          <Link to="/login" className="h-10 px-4 rounded-full btn-gold inline-flex items-center gap-1.5 text-xs font-bold" data-testid="mhd-signin">
            <LogIn className="h-4 w-4" />
            {t("sign_in")}
          </Link>
        )}

        <div className="flex-1 text-center px-3">
          <img
            src="/logo192.png"
            alt="ثقة"
            className="h-14 w-14 mx-auto rounded-full object-cover border border-[#D4AF37]/40 mb-1"
            data-testid="mhd-logo"
          />
          <div className="text-2xl font-black gold-text leading-tight">{t("mhd_platform")}</div>
          <div className="text-[11px] text-white/50 mt-0.5">{t("mhd_subtitle")}</div>
        </div>

        {/* Theme + lang + settings stacked */}
        <div className="flex flex-col gap-1.5">
          <ThemeSwitcher />
          <button
            type="button"
            onClick={toggle}
            className="h-10 w-10 rounded-full bg-white/5 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] transition hover:bg-white/10"
            aria-label={ar ? "Switch to English" : "التبديل إلى العربية"}
            data-testid="mhd-lang-toggle"
          >
            <span className="text-[10px] font-black">{ar ? "EN" : "ع"}</span>
          </button>
          <Link
            to="/settings"
            className="h-10 w-10 rounded-full bg-white/5 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] transition hover:bg-white/10"
            aria-label={ar ? "الإعدادات" : "Settings"}
            data-testid="mhd-settings"
          >
            <SettingsIcon className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Animal type pill + My Livestock quick link */}
      <div className="px-5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex-1 flex items-center justify-between gap-2 px-4 py-3 rounded-2xl glass-gold border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition"
          dir={ar ? "rtl" : "ltr"}
          data-testid="mhd-type-pill"
        >
          <div className="flex items-center gap-2">
            <span className="text-2xl">{animalMeta.emoji}</span>
            <div className="text-start">
              <div className="text-[10px] text-white/55 leading-none">{ar ? "نوع الحلال" : "Livestock type"}</div>
              <div className="text-sm font-black gold-text leading-tight">{ar ? animalMeta.ar : animalMeta.en}</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] text-[#D4AF37]/80 font-bold">
            <Repeat className="h-3.5 w-3.5" />
            {ar ? "تغيير" : "Change"}
          </span>
        </button>
        {user && (
          <Link
            to="/my-livestock"
            className="h-[58px] px-3 rounded-2xl glass-gold border border-[#D4AF37]/30 flex flex-col items-center justify-center gap-0.5 hover:-translate-y-0.5 transition"
            data-testid="mhd-my-livestock"
            aria-label={ar ? "حلالي" : "My livestock"}
          >
            <Camera className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[9px] font-bold text-white/80 leading-none">{ar ? "حلالي" : "Mine"}</span>
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="px-5 mt-3">
        <Link to="/catalog" className="flex items-center gap-2 px-4 py-3 rounded-2xl glass border border-[#D4AF37]/15" data-testid="mhd-search" dir={ar ? "rtl" : "ltr"}>
          <Search className="h-4 w-4 text-white/40" />
          <span className="text-sm text-white/40">{t("mhd_search_placeholder")}</span>
        </Link>
      </div>

      {/* Stats */}
      <section className="px-5 mt-4 grid grid-cols-2 gap-3" dir={ar ? "rtl" : "ltr"}>
        <StatTile icon={Users}    value={stats.users}       label={t("mhd_total_users")}   testid="mhd-stat-users" />
        <StatTile icon={Camera}   value={stats.animals}     label={t("mhd_total_animals")} testid="mhd-stat-animals" />
        <StatTile icon={Calendar} value={stats.evaluations} label={t("mhd_active_evals")}  testid="mhd-stat-evals" />
        <StatTile icon={Gavel}    value={stats.experts}     label={t("mhd_active_aucts")}  testid="mhd-stat-experts" />
      </section>

      {/* Influencer profile (only renders for users who applied) */}
      <div className="px-5 mt-4" dir={ar ? "rtl" : "ltr"}>
        <InfluencerStatusCard />
      </div>

      {/* AI Features banner — Compare / Market Value / Photo Studio */}
      <section className="px-5 mt-5" dir={ar ? "rtl" : "ltr"} data-testid="mhd-ai-features">
        <LiveCounter ar={ar} />

        {/* ✨ سوق ثقة — hero card, placed right under LiveCounter for
            maximum surface exposure. Whole card is clickable. */}
        <MarketShowcaseCard ar={ar} />

        <div className="flex items-center justify-between mt-4 mb-2">
          <h3 className="text-sm font-black gold-text">
            {ar ? "🤖 ميزات الذكاء الاصطناعي" : "🤖 AI Features"}
          </h3>
          <span className="text-[10px] font-black text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
            {ar ? "جديد" : "NEW"}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <AiTile to="/ai/compare" icon="⚖️" title={ar ? "قارن حلالك" : "Compare"} testid="ai-tile-compare" />
          <AiTile to="/ai/market-value" icon="💰" title={ar ? "كم يسوى" : "Value"} testid="ai-tile-value" />
          <AiTile to="/ai/photo-studio" icon="📸" title={ar ? "استديو" : "Studio"} testid="ai-tile-studio" />
        </div>
      </section>

      {/* Championships banner — flagship feature */}
      <section className="px-5 mt-5" dir={ar ? "rtl" : "ltr"}>
        <Link
          to="/championships"
          className="block relative overflow-hidden rounded-2xl border border-[#D4AF37]/40 shadow-[0_0_60px_-20px_rgba(212,175,55,0.55)]"
          style={{ background: "linear-gradient(135deg,#1a1300 0%,#0a0700 60%,#000 100%)" }}
          data-testid="mhd-championships-banner"
        >
          {/* Decorative gold glow */}
          <div className="absolute -top-12 -end-12 h-40 w-40 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#D4AF37 0%,transparent 70%)" }} />
          <div className="relative p-4 flex items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-[#D4AF37]/20 border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
              <Trophy className="h-7 w-7 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0" style={{ textAlign: ar ? "right" : "left" }}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[9px] font-black uppercase tracking-wider text-black bg-[#D4AF37] px-1.5 py-0.5 rounded">
                  {ar ? "قريباً" : "SOON"}
                </span>
                <span className="text-[10px] text-white/55 font-bold">
                  {ar ? "🐪 إبل · 🐎 خيل · 🦅 صقور" : "🐪 Camels · 🐎 Horses · 🦅 Falcons"}
                </span>
              </div>
              <h3 className="text-base font-black gold-text leading-tight">
                {ar ? "بطولات ثقة الرسمية" : "Thqah Official Championships"}
              </h3>
              <p className="text-[11px] text-white/65 mt-0.5 leading-snug">
                {ar ? "جوائز حتى 20,000 ر.س — سجّل اهتمامك" : "Prizes up to 20,000 SAR — register interest"}
              </p>
            </div>
            <ChevronLeft className={`h-5 w-5 text-[#D4AF37] shrink-0 ${ar ? "" : "rotate-180"}`} />
          </div>
        </Link>
      </section>

      {/* Services — 9-cell grid (3 cols × 3 rows). Racing mode removed. */}
      <section className="px-5 mt-5" dir={ar ? "rtl" : "ltr"}>
        <h2 className="text-base font-bold mb-3 text-white/90">
          {t("mhd_services_title")}
        </h2>
        <div className="grid grid-cols-3 gap-2">
          {baseTiles.map((s) => (
            <ServiceTile key={s.key} s={s} />
          ))}
        </div>
      </section>

      {/* Hero */}
      <section className="px-5 mt-6" dir={ar ? "rtl" : "ltr"}>
        <Link to="/leaderboard" className="block glass-gold rounded-2xl border border-[#D4AF37]/30 overflow-hidden relative h-40" data-testid="mhd-hero-card">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/33429147/pexels-photo-33429147.jpeg)" }} />
          <div className="absolute inset-0" style={{ background: ar ? "linear-gradient(270deg, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 80%)" : "linear-gradient(90deg, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 80%)" }} />
          <div className="absolute inset-0 p-4 flex flex-col justify-center" style={{ alignItems: ar ? "flex-end" : "flex-start", textAlign: ar ? "right" : "left" }}>
            <div className="text-base font-bold gold-text">{t("mhd_hero_title")}</div>
            <div className="text-[11px] text-white/70 mt-0.5 max-w-[180px] leading-snug">{t("mhd_hero_sub")}</div>
            <span className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold btn-gold">
              {t("mhd_hero_cta")} {ar ? null : <ChevronLeft className="h-3 w-3 rotate-180" />}
            </span>
          </div>
        </Link>
      </section>

      {/* Recent activity */}
      <section className="px-5 mt-7" dir={ar ? "rtl" : "ltr"}>
        <h2 className="text-base font-bold mb-3 text-white/90">{t("mhd_recent_title")}</h2>
        <div className="rounded-2xl glass border border-[#D4AF37]/15 overflow-hidden divide-y divide-white/5">
          {(activities.length === 0 ? [
            { id: "p1", title_ar: t("mhd_act_eval_done"),     title_en: t("mhd_act_eval_done"),     ts: null, emoji: animalMeta.emoji },
            { id: "p2", title_ar: t("mhd_act_market_listed"), title_en: t("mhd_act_market_listed"), ts: null, emoji: "🏆" },
            { id: "p3", title_ar: t("mhd_act_expert_review"), title_en: t("mhd_act_expert_review"), ts: null, emoji: "👑" },
          ] : activities.slice(0, 4)).map((a) => (
            <div key={a.id} className="px-3 py-3 flex items-center gap-3" data-testid={`mhd-activity-${a.id}`}>
              <div className="h-9 w-9 rounded-full bg-white/5 border border-[#D4AF37]/30 flex items-center justify-center text-base shrink-0">{a.emoji || "✦"}</div>
              <div className="flex-1 min-w-0" style={{ textAlign: ar ? "right" : "left" }}>
                <div className="text-sm text-white/90 font-medium truncate">{ar ? (a.title_ar || a.title_en) : (a.title_en || a.title_ar)}</div>
                <div className="text-[11px] text-white/45 mt-0.5">{a.ts ? fmtTime(a.ts) : (a.time || "—")}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ServiceTile({ s }) {
  const { lang } = useI18n();
  const ar = lang === "ar";
  const isSub = s.key === "subscribe";
  const cs = !!s.comingSoon;
  // Coming-soon tiles: show a toast instead of navigating to a placeholder page.
  const handleClick = (e) => {
    if (cs) {
      e.preventDefault();
      toast.info(ar ? "هذه الخدمة قيد التطوير — قريباً" : "This service is in development — coming soon");
      return;
    }
    if (s.external === "whatsapp_influencer") {
      e.preventDefault();
      applyAsInfluencer();
    }
  };
  return (
    <Link
      to={cs || s.external ? "#" : (s.to || "#")}
      onClick={handleClick}
      className={
        "relative rounded-2xl p-3 flex flex-col items-center gap-2 hover:-translate-y-0.5 transition " +
        (isSub
          ? "border-2 border-[#D4AF37] shadow-[0_0_24px_-6px_rgba(212,175,55,0.55)]"
          : cs
            ? "border border-white/10 opacity-70 grayscale-[0.4]"
            : "glass-gold border border-[#D4AF37]/25")
      }
      style={isSub ? { background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.04))" } : undefined}
      data-testid={`mhd-svc-${s.key}`}
      data-coming-soon={cs ? "1" : undefined}
    >
      <div
        className="h-11 w-11 rounded-xl flex items-center justify-center"
        style={{
          background: isSub ? "linear-gradient(135deg,#D4AF37,#9a7a1f)" : "linear-gradient(135deg,#1a1a1a,#0a0a0a)",
          border: isSub ? "1px solid #f0d77a" : (cs ? "1px solid rgba(212,175,55,0.4)" : "1px solid #D4AF37"),
        }}
      >
        <s.icon className={`h-5 w-5 ${isSub ? "text-black" : cs ? "text-[#D4AF37]/70" : "text-[#D4AF37]"}`} />
      </div>
      <div className={`text-[10px] text-center leading-tight font-medium ${isSub ? "text-[#D4AF37] font-bold" : cs ? "text-white/65" : "text-white/85"}`}>
        {s.label}
      </div>
      {s.badge && (
        <span
          className={
            "absolute -top-2 end-1 px-1.5 py-0.5 rounded-full text-[8px] font-black border leading-none " +
            (cs
              ? "bg-amber-500/15 text-amber-300 border-amber-400/40"
              : "bg-black text-[#D4AF37] border-[#D4AF37]/60")
          }
          data-testid={`mhd-svc-${s.key}-badge`}
        >
          {s.badge}
        </span>
      )}
    </Link>
  );
}

function StatTile({ icon: Icon, value, label, testid }) {
  return (
    <div className="rounded-2xl border border-[#D4AF37]/20 p-3 stat-tile" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-[#D4AF37]" />
        <div className="text-2xl font-black gold-text">{Number(value || 0).toLocaleString()}</div>
      </div>
      <div className="text-[11px] text-white/60 mt-1">{label}</div>
    </div>
  );
}

function AiTile({ to, icon, title, testid }) {
  return (
    <Link
      to={to}
      className="aspect-square rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/15 to-black/40 flex flex-col items-center justify-center gap-1 active:scale-[0.97] transition"
      data-testid={testid}
    >
      <span className="text-2xl leading-none">{icon}</span>
      <span className="text-[11px] font-black text-[#D4AF37]">{title}</span>
    </Link>
  );
}


/* ─────────── سوق ثقة hero card (below LiveCounter, above AI Features) ─────────── */
function MarketShowcaseCard({ ar }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/marketplace/showcase")
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const active   = stats?.active_listings   ?? 0;
  const animals  = stats?.total_animals     ?? 0;
  const sold     = stats?.sold_count        ?? 0;
  const latest   = stats?.latest_listing;   // { title, category, created_at }

  const nudge = (n) => (n > 0 ? Number(n).toLocaleString() : "—");

  return (
    <Link
      to="/marketplace"
      className="mt-4 block relative overflow-hidden rounded-3xl border border-[#D4AF37]/50
                 bg-gradient-to-br from-[#D4AF37]/25 via-[#3a2a10]/60 to-black
                 shadow-[0_0_60px_-20px_rgba(212,175,55,0.55)] active:scale-[0.99] transition"
      data-testid="market-showcase-card"
      dir={ar ? "rtl" : "ltr"}
    >
      <span className="absolute -top-16 -end-16 h-40 w-40 rounded-full bg-[#D4AF37]/30 blur-3xl pointer-events-none" />

      <div className="relative p-5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="inline-flex items-center gap-1.5 text-[#D4AF37]">
            <ShoppingBag className="h-4 w-4" />
            <span className="font-black text-lg">
              {ar ? "🛒 سوق ثقة" : "🛒 Thqah Market"}
            </span>
          </div>
          <span className="text-[9px] font-black bg-emerald-500 text-black px-2 py-0.5 rounded-full">
            {ar ? "الأكثر رواجاً" : "TRENDING"}
          </span>
        </div>

        <p className="text-white/80 text-[12.5px] leading-relaxed mb-3.5">
          {ar
            ? "بيع واشترِ الإبل والخيل والصقور والغنم والماعز بكل سهولة داخل منصة ثقة."
            : "Buy and sell camels, horses, falcons, sheep and goats — right inside Thqah."}
        </p>

        <div className="grid grid-cols-3 gap-2 mb-3.5" data-testid="market-showcase-stats">
          <ShowcaseStat label={ar ? "إعلان نشط" : "active"}   value={nudge(active)}  />
          <ShowcaseStat label={ar ? "حلال معروض" : "animals"}  value={nudge(animals)} />
          <ShowcaseStat label={ar ? "عملية بيع" : "sold"}      value={nudge(sold)}    />
        </div>

        {latest && (
          <div
            className="mb-3.5 flex items-center gap-2 rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-[11px]"
            data-testid="market-showcase-latest"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/60">{ar ? "آخر إعلان:" : "Latest:"}</span>
            <span className="text-white font-bold truncate flex-1">{latest.title}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2" data-testid="market-showcase-ctas">
          <span
            className="rounded-full bg-white/10 border border-white/20 text-white font-black py-2.5 text-sm inline-flex items-center justify-center gap-1.5"
            data-testid="market-browse-btn"
          >
            <ArrowLeft className={`h-4 w-4 ${ar ? "" : "rotate-180"}`} />
            {ar ? "تصفح السوق" : "Browse"}
          </span>
          <Link
            to="/marketplace/thqah_market/camel/new"
            onClick={(e) => e.stopPropagation()}
            className="rounded-full bg-[#D4AF37] text-black font-black py-2.5 text-sm inline-flex items-center justify-center gap-1.5 shadow-[0_10px_25px_-5px_rgba(212,175,55,0.55)]"
            data-testid="market-add-listing-btn"
          >
            <Plus className="h-4 w-4" />
            {ar ? "أضف إعلانك" : "Add listing"}
          </Link>
        </div>
      </div>
    </Link>
  );
}

function ShowcaseStat({ label, value }) {
  return (
    <div className="rounded-xl bg-black/40 border border-[#D4AF37]/25 py-2 px-1 text-center">
      <div className="text-lg font-black text-[#D4AF37] leading-none">{value}</div>
      <div className="text-[9.5px] text-white/60 mt-1 tracking-wide">{label}</div>
    </div>
  );
}
